import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ConversationMessageWithStatus } from '../../../types/conversation.types';
import { translateMessage } from '../conversationTransport';

/** What a message's translation looks like to the bubble showing it. */
export type MessageTranslationState =
  | { status: 'loading' }
  /** The translation is what the bubble shows. */
  | { status: 'shown'; text: string; sourceLanguage: string }
  /** Translated, but the reader went back to the original. */
  | { status: 'original' }
  | { status: 'failed'; error: string; retryable: boolean };

type Entry = {
  /**
   * The text that was sent for translation. An edit changes the body under
   * the same id, and a translation of words that are no longer there is worse
   * than none - so an entry only counts while the body still matches.
   */
  sourceBody: string;
} & (
  | { status: 'loading' }
  | { status: 'done'; text: string; sourceLanguage: string; showing: boolean }
  | { status: 'failed'; error: string; retryable: boolean }
);

/** The spec's copy for each failure; anything unexpected reads as retryable. */
const failureFor = (error: unknown) => {
  const status = axios.isAxiosError(error) ? error.response?.status : null;
  if (status === 403) {
    return { error: "You don't have access to this chat.", retryable: false };
  }
  if (status === 404) return { error: 'Message not found.', retryable: false };
  return { error: 'Translation failed.', retryable: true };
};

/**
 * Translate / See original, per message, for the open thread.
 *
 * Held in memory only, for as long as the thread is open: the backend caches
 * every translation, so asking again in a later session is instant and free,
 * and the result is the reader's alone - nothing about it is sent with the
 * messages or reaches anyone else in the conversation.
 *
 * Only on a click, never for the whole list on open: the first translation of
 * a text is an AI call of a second or three.
 */
export const useMessageTranslations = (
  conversationId: number | string | null
) => {
  const [entries, setEntries] = useState<Record<number, Entry>>({});

  /* The thread on screen, for an answer that lands after the reader moved on -
     it must not be written into the next thread's (just emptied) memory. */
  const openThreadRef = useRef(conversationId);

  // Translations belong to the thread they were asked for in.
  useEffect(() => {
    openThreadRef.current = conversationId;
    setEntries({});
  }, [conversationId]);

  const stateOf = useCallback(
    (message: ConversationMessageWithStatus): MessageTranslationState | null => {
      const entry = entries[message.id];
      if (!entry || entry.sourceBody !== message.body) return null;
      if (entry.status === 'done') {
        return entry.showing
          ? {
              status: 'shown',
              text: entry.text,
              sourceLanguage: entry.sourceLanguage,
            }
          : { status: 'original' };
      }
      return entry;
    },
    [entries]
  );

  const toggle = useCallback(
    async (message: ConversationMessageWithStatus) => {
      if (conversationId === null || !message.body) return;
      const sourceBody = message.body;
      const current = entries[message.id];
      const fresh = current?.sourceBody === sourceBody ? current : null;

      if (fresh?.status === 'loading') return;
      /* Back and forth from memory - the translation is already here. */
      if (fresh?.status === 'done') {
        setEntries((all) => ({
          ...all,
          [message.id]: { ...fresh, showing: !fresh.showing },
        }));
        return;
      }

      const write = (entry: Entry) => {
        if (openThreadRef.current !== conversationId) return;
        setEntries((all) => ({ ...all, [message.id]: entry }));
      };

      write({ status: 'loading', sourceBody });
      try {
        const { data } = await translateMessage(conversationId, message.id);
        write({
          status: 'done',
          text: data.data.text,
          sourceLanguage: data.data.sourceLanguage,
          showing: true,
          sourceBody,
        });
      } catch (error) {
        write({ status: 'failed', ...failureFor(error), sourceBody });
      }
    },
    [conversationId, entries]
  );

  return { stateOf, toggle };
};
